import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  Loader2,
  RotateCcw,
  Save,
  Trash2,
} from "lucide-react";
import { api, formatApiError } from "@/api/client";
import AppNav from "@/components/app/AppNav";
import AIDisclosureBadge from "@/components/AIDisclosureBadge";
import MailLetterCard from "@/components/MailLetterCard";
import { Badge, Breadcrumbs, Button, Input, Textarea } from "@/design-system";

const LABEL = {
  experian: "Experian",
  equifax: "Equifax",
  transunion: "TransUnion",
  furnisher: "Furnisher",
  collection_agency: "Collection agency",
};

export default function DisputeLetterDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [letter, setLetter] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // Local drafts of editable fields. Synced to `letter` on load.
  const [subject, setSubject] = useState("");
  const [recipientAddress, setRecipientAddress] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [edits, setEdits] = useState({}); // negative_item_id -> paragraph

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/dispute-letters/${id}`)
      .then((r) => {
        if (cancelled) return;
        setLetter(r.data);
        setSubject(r.data?.subject_line || "");
        setRecipientAddress(r.data?.recipient_address || "");
        setAttachments(r.data?.attachment_checklist || []);
        const seed = {};
        (r.data?.disputed_items || []).forEach((it) => {
          seed[it.negative_item_id] =
            it.user_edited_paragraph || it.ai_drafted_paragraph || "";
        });
        setEdits(seed);
      })
      .catch((err) => !cancelled && setError(formatApiError(err)));
    return () => {
      cancelled = true;
    };
  }, [id]);

  const downloadUrl = (fmt) => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    return `${base}/api/dispute-letters/${id}/download?format=${fmt}`;
  };

  const resetItem = (itemId) => {
    const it = (letter?.disputed_items || []).find((x) => x.negative_item_id === itemId);
    if (!it) return;
    setEdits((prev) => ({ ...prev, [itemId]: it.ai_drafted_paragraph || "" }));
  };

  const save = async () => {
    if (!letter) return;
    setSaving(true);
    setError("");
    const item_edits = (letter.disputed_items || [])
      .map((it) => {
        const current = edits[it.negative_item_id] || "";
        const drift = current.trim() !== (it.ai_drafted_paragraph || "").trim();
        return {
          negative_item_id: it.negative_item_id,
          user_edited_paragraph: drift ? current : "",
        };
      })
      .filter((e) => e.user_edited_paragraph !== undefined);
    try {
      const res = await api.patch(`/dispute-letters/${id}`, {
        subject_line: subject,
        recipient_address: recipientAddress,
        attachment_checklist: attachments,
        item_edits,
      });
      setLetter(res.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this letter? This cannot be undone.")) return;
    try {
      await api.delete(`/dispute-letters/${id}`);
      navigate("/dispute-letters");
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  if (error && !letter) {
    return (
      <main className="mx-auto max-w-4xl px-5 md:px-8 py-10">
        <AppNav />
        <p className="mt-10 text-[13px]" style={{ color: "var(--ds-color-danger)" }}>
          {error}
        </p>
      </main>
    );
  }

  if (!letter) {
    return (
      <main className="mx-auto max-w-4xl px-5 md:px-8 py-10">
        <AppNav />
        <p className="mt-10 text-[13px] text-[var(--ds-color-ink-muted)]">Loading…</p>
      </main>
    );
  }

  return (
    <main className="min-h-screen text-[var(--ds-color-ink)] pb-20 bg-[var(--ds-color-background)]" data-testid="dispute-letter-detail">
      <AppNav />
      <div className="mx-auto max-w-4xl px-4 md:px-8 pt-10">
        <Breadcrumbs
          items={[
            { label: "Dispute Center", to: "/dispute-letters" },
            { label: LABEL[letter.letter_type] || letter.letter_type || "Draft letter" },
          ]}
        />
        <Link
          to="/dispute-letters"
          className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] transition-colors"
          style={{ transitionDuration: "var(--ds-duration-fast)" }}
        >
          <ArrowLeft className="w-3.5 h-3.5" aria-hidden /> Back to letters
        </Link>

        <header className="mt-6">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--ds-color-ink-muted)] flex items-center gap-3 flex-wrap">
            <span>Credit Repair · Draft letter</span>
            <AIDisclosureBadge />
          </div>
          <div className="mt-2 flex items-start justify-between gap-4 flex-wrap">
            <h1
              className="text-[var(--ds-color-ink)] leading-tight"
              style={{
                fontFamily: "var(--ds-font-display)",
                fontWeight: 600,
                letterSpacing: "-0.02em",
                fontSize: "clamp(1.75rem, 3vw, 2.5rem)",
              }}
            >
              {LABEL[letter.letter_type] || letter.letter_type} dispute letter.
            </h1>
            <Badge tone={letter.status === "finalized" ? "success" : "accent"} size="md">
              {letter.status}
            </Badge>
          </div>
        </header>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          {["pdf", "docx", "txt"].map((fmt) => (
            <a
              key={fmt}
              href={downloadUrl(fmt)}
              data-testid={`dispute-letter-detail-download-${fmt}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[var(--ds-radius-md)] text-[12px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] focus-visible:ring-offset-2"
              style={{
                background: "var(--ds-color-surface-muted)",
                border: "1px solid var(--ds-color-border-strong)",
                color: "var(--ds-color-ink)",
                transitionDuration: "var(--ds-duration-fast)",
              }}
            >
              <Download className="w-3.5 h-3.5" aria-hidden /> Download {fmt.toUpperCase()}
            </a>
          ))}
          <Button
            variant="primary"
            size="sm"
            data-testid="dispute-letter-detail-save"
            onClick={save}
            disabled={saving}
            loading={saving}
          >
            <Save className="w-3.5 h-3.5" aria-hidden />
            Save changes
          </Button>
          <button
            type="button"
            data-testid="dispute-letter-detail-delete"
            onClick={handleDelete}
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[var(--ds-radius-md)] text-[12px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] focus-visible:ring-offset-2"
            style={{
              border: "1px solid var(--ds-color-danger)",
              color: "var(--ds-color-danger)",
              transitionDuration: "var(--ds-duration-fast)",
            }}
          >
            <Trash2 className="w-3.5 h-3.5" aria-hidden /> Delete
          </button>
        </div>

        {error && (
          <div
            className="mt-6 rounded-[var(--ds-radius-md)] p-4 text-[13px]"
            role="alert"
            style={{
              background: "var(--ds-color-danger-soft)",
              border: "1px solid var(--ds-color-danger)",
              color: "var(--ds-color-danger)",
            }}
          >
            {error}
          </div>
        )}

        <MailLetterCard
          letter={letter}
          onMailed={() => {
            // Re-fetch the letter so mailing_status / latest_mailed_letter_id
            // are surfaced in the rest of the page.
            api.get(`/dispute-letters/${id}`).then((r) => setLetter(r.data)).catch(() => {});
          }}
        />

        <section
          className="mt-8 rounded-[var(--ds-radius-lg)] p-6 md:p-8 space-y-6"
          style={{
            background: "var(--ds-color-surface)",
            border: "1px solid var(--ds-color-border)",
            boxShadow: "var(--ds-shadow-card)",
          }}
        >
          <div className="text-right text-[13px] text-[var(--ds-color-ink-muted)]">
            {letter.letter_date}
          </div>

          <FieldGroup label="Recipient">
            <Textarea
              data-testid="detail-recipient-address"
              value={`${letter.recipient_name}\n${recipientAddress}`}
              onChange={(e) => {
                const [, ...rest] = e.target.value.split("\n");
                setRecipientAddress(rest.join("\n"));
              }}
              rows={3}
            />
          </FieldGroup>

          <FieldGroup label="Subject">
            <Input
              data-testid="detail-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </FieldGroup>

          <p className="text-[13px] text-[var(--ds-color-ink-muted)]">
            Dear Consumer Dispute Department,
          </p>
          <p className="text-[13px] leading-relaxed text-[var(--ds-color-ink)]">
            {letter.intro_paragraph}
          </p>

          <ol
            data-testid="detail-disputed-items"
            className="space-y-4 list-none"
          >
            {(letter.disputed_items || []).map((it, idx) => (
              <li
                key={it.negative_item_id}
                data-testid={`detail-item-${it.negative_item_id}`}
                className="rounded-[var(--ds-radius-md)] p-4"
                style={{
                  background: "var(--ds-color-surface-muted)",
                  border: "1px solid var(--ds-color-border)",
                }}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className="text-[var(--ds-color-ink)]"
                    style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "1rem" }}
                  >
                    {idx + 1}. {it.account_or_record_label}
                  </span>
                  {it.ai_assist_failed ? (
                    <AIDisclosureBadge inline tone="warning" />
                  ) : (
                    <AIDisclosureBadge inline />
                  )}
                </div>
                <div className="mt-2 text-[12px] text-[var(--ds-color-ink-muted)]">
                  Creditor / Furnisher: {it.creditor_or_furnisher}
                  {it.balance_or_amount != null && (
                    <> · Balance: ${it.balance_or_amount.toFixed(2)}</>
                  )}
                  {it.date_of_first_delinquency && (
                    <> · DOFD: {it.date_of_first_delinquency}</>
                  )}
                </div>
                <div className="mt-3">
                  <label className="block text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)] mb-1.5">
                    Dispute paragraph
                  </label>
                  <Textarea
                    data-testid={`detail-item-paragraph-${it.negative_item_id}`}
                    value={edits[it.negative_item_id] ?? ""}
                    onChange={(e) =>
                      setEdits((prev) => ({
                        ...prev,
                        [it.negative_item_id]: e.target.value,
                      }))
                    }
                    rows={4}
                  />
                  <button
                    type="button"
                    data-testid={`detail-item-reset-${it.negative_item_id}`}
                    onClick={() => resetItem(it.negative_item_id)}
                    className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-accent)] transition-colors"
                    style={{ transitionDuration: "var(--ds-duration-fast)" }}
                  >
                    <RotateCcw className="w-3 h-3" aria-hidden /> Reset to AI draft
                  </button>
                </div>
                <div className="mt-2 text-[11px] text-[var(--ds-color-ink-muted)]">
                  Legal basis: {it.fcra_reference}
                </div>
              </li>
            ))}
          </ol>

          <pre className="text-[13px] leading-relaxed text-[var(--ds-color-ink)] whitespace-pre-wrap font-sans">
            {letter.request_paragraph}
          </pre>

          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)] mb-2">
              Attachment checklist
            </label>
            <ul className="space-y-1.5">
              {attachments.map((a, i) => (
                <li key={i} className="flex items-center gap-2">
                  <Input
                    data-testid={`detail-attachment-${i}`}
                    type="text"
                    className="min-w-0 flex-1"
                    value={a}
                    onChange={(e) => {
                      const next = [...attachments];
                      next[i] = e.target.value;
                      setAttachments(next);
                    }}
                  />
                  <button
                    type="button"
                    data-testid={`detail-attachment-remove-${i}`}
                    onClick={() =>
                      setAttachments(attachments.filter((_, j) => j !== i))
                    }
                    className="text-[12px] px-2 py-1 rounded-[var(--ds-radius-md)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] focus-visible:ring-offset-2"
                    style={{
                      border: "1px solid var(--ds-color-danger)",
                      color: "var(--ds-color-danger)",
                      transitionDuration: "var(--ds-duration-fast)",
                    }}
                  >
                    Remove
                  </button>
                </li>
              ))}
              <li>
                <button
                  type="button"
                  data-testid="detail-attachment-add"
                  onClick={() => setAttachments([...attachments, ""])}
                  className="mt-1 text-[12px] font-semibold transition-colors"
                  style={{ color: "var(--ds-color-accent)", transitionDuration: "var(--ds-duration-fast)" }}
                >
                  + Add attachment
                </button>
              </li>
            </ul>
          </div>

          <pre className="text-[13px] leading-relaxed text-[var(--ds-color-ink-muted)] whitespace-pre-wrap font-sans">
            {letter.signature_line}
            {"\n\n"}
            {letter.user_address_line1}
            {letter.user_address_line2 ? `\n${letter.user_address_line2}` : ""}
            {`\n${letter.user_city}, ${letter.user_state} ${letter.user_zip}`}
            {letter.user_phone ? `\n${letter.user_phone}` : ""}
            {`\n${letter.user_email}`}
          </pre>
        </section>
      </div>
    </main>
  );
}

function FieldGroup({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
        {label}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
